import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import { companyInfo, cookieSections } from './cookieContent';

import css from './CookiePolicyPage.module.css';

import { ReactComponent as DownloadIcon } from '../../assets/svg/downloadIcon.svg';

const renderContent = value => {
  if (typeof value !== 'string') {
    return value;
  }

  return value.startsWith('CookiePolicyPage.') ? <FormattedMessage id={value} /> : value;
};

const Paragraph = ({ id, text }) => (
  <p className={css.paragraph}>{id ? renderContent(id) : text}</p>
);

const Subheading = ({ id }) => <h3 className={css.subheading}>{renderContent(id)}</h3>;

// Bolds the lead-in before the first colon, e.g. "Google Analytics: website
// usage statistics" -> **Google Analytics:** website usage statistics.
const BoldLeadText = ({ text }) => {
  const colonIndex = text.indexOf(':');
  if (colonIndex === -1) {
    return text;
  }

  return (
    <>
      <span className={css.boldLead}>{text.slice(0, colonIndex + 1)}</span>
      {text.slice(colonIndex + 1)}
    </>
  );
};

const renderListItem = item => {
  if (typeof item !== 'string') {
    return item;
  }

  return item.startsWith('CookiePolicyPage.') ? (
    <FormattedMessage id={item}>{chunks => <BoldLeadText text={chunks.join('')} />}</FormattedMessage>
  ) : (
    <BoldLeadText text={item} />
  );
};

const BulletList = ({ items, itemIds }) => {
  const listItems = items || itemIds || [];

  return (
    <ul className={css.bulletList}>
      {listItems.map((item, index) => (
        <li key={typeof item === 'string' ? item : index}>{renderListItem(item)}</li>
      ))}
    </ul>
  );
};

const OrderedList = ({ itemIds }) => (
  <ol className={css.orderedList}>
    {itemIds.map((item, index) => (
      <li key={typeof item === 'string' ? item : index}>{renderListItem(item)}</li>
    ))}
  </ol>
);

const KeyValueTable = ({ rows }) => (
  <div className={css.tableWrap}>
    <table className={css.table}>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index}>
            <td className={css.tableLabelCell}>{renderContent(row[0])}</td>
            <td>{renderContent(row[1])}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const DataTable = ({ headerIds, rows }) => (
  <div className={css.tableWrap}>
    <table className={css.table}>
      <thead>
        <tr>
          {headerIds.map(id => (
            <th key={id}>{renderContent(id)}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={rowIndex}>
            {row.map((cell, cellIndex) => (
              <td key={cellIndex}>{renderContent(cell)}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Box = ({ title, titleId, body, bodyId }) => (
  <div className={css.box}>
    {title || titleId ? <p className={css.boxTitle}>{renderContent(title || titleId)}</p> : null}

    <Paragraph id={bodyId} text={body} />
  </div>
);

const ContactSection = ({ cards }) => (
  <div className={css.contactGrid}>
    {cards.map(card => (
      <div className={css.contactCard} key={card.headingId}>
        <p className={css.contactCardHeading}>{renderContent(card.headingId)}</p>
        {card.rows.map((row, index) => (
          <div className={css.contactRow} key={index}>
            {row.labelId ? (
              <span className={css.contactLabel}>{renderContent(row.labelId)}</span>
            ) : null}
            <span className={css.contactValue}>{row.value}</span>
          </div>
        ))}
      </div>
    ))}
  </div>
);

const Block = ({ block }) => {
  switch (block.type) {
    case 'p':
      return <Paragraph id={block.id} />;

    case 'ul':
      return <BulletList itemIds={block.itemIds} />;

    case 'ol':
      return <OrderedList itemIds={block.itemIds} />;

    case 'subheading':
      return <Subheading id={block.id} />;

    case 'contact':
      return <ContactSection cards={block.cards} />;

    case 'box':
      return (
        <Box title={block.title} titleId={block.titleId} body={block.body} bodyId={block.bodyId} />
      );

    case 'kvTable':
      return <KeyValueTable rows={block.rows} />;

    case 'table':
      return <DataTable headerIds={block.headerIds} rows={block.rows} />;

    case 'callout':
      return (
        <Box title={block.title} titleId={block.titleId} body={block.body} bodyId={block.bodyId} />
      );

    default:
      return null;
  }
};

const Section = ({ section }) => {
  const content = (
    <>
      <h2 className={css.sectionHeading}>
        {section.number}. {renderContent(section.titleId)}
      </h2>

      {section.blocks.map((block, index) => (
        <Block key={index} block={block} />
      ))}
    </>
  );

  return (
    <div className={css.section}>
      {section.highlight ? (
        <div className={css.highlightBlock}>{content}</div>
      ) : section.gradient ? (
        <div className={css.gradientBlock}>{content}</div>
      ) : section.accentBar ? (
        <div className={css.accentBarBlock}>{content}</div>
      ) : (
        content
      )}
    </div>
  );
};

/**
 * Cookie Policy Content
 */

export const CookiePolicyContent = () => {
  const contactIndex = cookieSections.findIndex(section => section.number === '11');

  const cardSections = contactIndex === -1 ? cookieSections : cookieSections.slice(0, contactIndex);

  const afterCardSections = contactIndex === -1 ? [] : cookieSections.slice(contactIndex);

  return (
    <>
      <div className={css.card}>
        {cardSections.map(section => (
          <Section key={section.number} section={section} />
        ))}
      </div>

      {afterCardSections.length > 0 && (
        <div className={css.cardFooter}>
          {afterCardSections.map(section => (
            <Section key={section.number} section={section} />
          ))}
        </div>
      )}
    </>
  );
};

export const CookiePolicyPageComponent = props => {
  const { scrollingDisabled } = props;

  const intl = useIntl();
  const history = useHistory();

  const handleDownloadPdf = () => {
    if (typeof window !== 'undefined') {
      window.print();
    }
  };

  return (
    <Page
      title={intl.formatMessage({ id: 'CookiePolicyPage.title' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <div className={css.heroSection}>
            <div className={css.topRow}>
              <button className={css.backLink} type="button" onClick={() => history.goBack()}>
                <FormattedMessage id="CookiePolicyPage.backBtn" />
              </button>

              <button className={css.downloadButton} type="button" onClick={handleDownloadPdf}>
                <DownloadIcon className={css.icon} />

                <FormattedMessage id="CookiePolicyPage.downloadBtn" />
              </button>
            </div>

            <h1 className={css.title}>
              <FormattedMessage id="CookiePolicyPage.title" />
            </h1>

            <div className={css.subtitleRow}>
              <span>{companyInfo.name}</span>

              <span className={css.subtitleDivider}>|</span>

              <span>
                <FormattedMessage id="CookiePolicyPage.effectiveDate" />:{' '}
                {companyInfo.effectiveDate}
              </span>

              <span className={css.subtitleDivider}>|</span>

              <span>
                <FormattedMessage id="CookiePolicyPage.version" />: {companyInfo.version}
              </span>
            </div>
          </div>

          <div className={css.content}>
            <CookiePolicyContent />
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const CookiePolicyPage = connect(mapStateToProps)(CookiePolicyPageComponent);

export default CookiePolicyPage;
